import React, { memo, forwardRef, useRef, useLayoutEffect, useImperativeHandle } from 'react'
// import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import Exporting from 'highcharts/modules/exporting'
import PatternFill from '@aytm-public/_hc/plugins/pattern-fill-v2'

Exporting(Highcharts)
PatternFill(Highcharts)

type ChartConstructor = (
  renderTo: string | HTMLElement,
  options: Highcharts.Options,
  callback?: Highcharts.ChartCallbackFunction
) => Highcharts.Chart

type ChartFactoryName = {
  [K in keyof typeof Highcharts]: (typeof Highcharts)[K] extends ChartConstructor ? K : never
}[keyof typeof Highcharts]

type HighchartsReactProps<K extends ChartFactoryName = 'chart'> = {
  highcharts: typeof Highcharts
  constructorType?: K
  options: Highcharts.Options
  callback?: Highcharts.ChartCallbackFunction
  immutable?: boolean
  updateArgs?: [boolean, boolean?]
  allowChartUpdate?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export const HighchartsReact = memo(
  forwardRef(function HighchartsReact<K extends ChartFactoryName = 'chart'>(
    {
      highcharts: propsHighcharts = Highcharts,
      constructorType: propsConstructorType = 'chart' as K,
      options,
      callback,
      immutable,
      updateArgs,
      allowChartUpdate,
      ...containerProps
    }: HighchartsReactProps<K>,
    ref: React.Ref<{ chart: Highcharts.Chart | null; container: React.RefObject<HTMLElement> }>
  ) {
    const containerRef = useRef<HTMLElement>()
    const chartRef = useRef<Highcharts.Chart>()
    const constructorType = useRef<HighchartsReactProps<K>['constructorType']>(propsConstructorType)
    const highcharts = useRef<typeof Highcharts | null>(propsHighcharts)

    useLayoutEffect(() => {
      function createChart() {
        const H = propsHighcharts || Highcharts
        const constructorType = propsConstructorType || 'chart'
        const chartFactory: ChartConstructor = H[constructorType]
        chartRef.current = chartFactory(containerRef.current, options, callback)
      }

      if (!chartRef.current) {
        createChart()
      } else {
        if (allowChartUpdate !== false) {
          // Reacreate chart on Highcharts or constructor type change
          if (propsConstructorType !== constructorType.current || propsHighcharts !== highcharts.current) {
            constructorType.current = propsConstructorType
            highcharts.current = propsHighcharts
            createChart()
            // Use `chart.update` to apply changes
          } else if (!immutable && chartRef.current) {
            chartRef.current.update(options, ...(updateArgs || [true, true]))
          } else {
            createChart()
          }
        }
      }
    }, [
      options,
      propsHighcharts,
      constructorType,
      callback,
      immutable,
      updateArgs,
      allowChartUpdate,
      propsConstructorType
    ])

    useLayoutEffect(
      () => () => {
        if (chartRef.current) {
          chartRef.current.destroy()
          chartRef.current = null
        }
      },
      []
    )

    useImperativeHandle(
      ref,
      () =>
        ({
          get chart() {
            return chartRef.current
          },
          container: containerRef
        }) as const,
      []
    )

    return <div ref={containerRef as React.RefObject<HTMLDivElement>} {...containerProps} />
  })
)

HighchartsReact.displayName = 'HighchartsReact'

// HighchartsReact.propTypes = {
//   highcharts: PropTypes.object,
//   constructorType: PropTypes.string,
//   options: PropTypes.object.isRequired,
//   callback: PropTypes.func,
//   immutable: PropTypes.bool,
//   updateArgs: PropTypes.array,
//   containerProps: PropTypes.object,
//   allowChartUpdate: PropTypes.bool
// }
